'use strict';

(function (global) {

  /**
   * Класс группы кнопок React-панели действий.
   * @memberOf WebAccess.components.wrappers.toolBar
   * @class ReactToolBarGroup
   * @extends BaseReactPageElement
   * @since 5.6.0.1300
   */
  var ReactToolBarGroup = global.components.wrappers.BaseReactPageElement.extend({

    /**
     * Конструктор.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#init
     * @param {object} model Модель группы кнопок на панели действий.
     * @override
     */
    init(model) {
      let extendedModel = this._extendModel(model);
      this._super(extendedModel);
      this.priority = this._model.priority;
    },

    /**
     * Имя класса.
     *
     * @private
     * @property WebAccess.components.wrappers.toolBar.ReactToolBarGroup#className
     * @override
     */
    className: 'ReactToolBarGroup',

    /**
     * Приоритет группы кнопок на панели действий.
     *
     * @private
     * @property WebAccess.components.wrappers.ReactToolBarGroup#priority
     */
    priority: null,

    /**
     * Расширить модель группы кнопок на панели действий.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#_extendModel
     * @param {object} model Модель группы кнопок на панели действий.
     * @returns {object} Расширенная модель группы кнопок на панели действий.
     */
    _extendModel(model) {
      model.isCollapsed = model.isCollapsed || false;
      return model;
    },

    /**
     * Создает кнопку в текущей группе кнопок.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#createButton
     * @param {string} buttonName Имя создаваемой кнопки.
     * @param {BUTTON} [opt_smallSized] Отображение или скрытие заголовка кнопки.
     * @return {object} Объект типа ReactToolBarButton.
     * @fires WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT#CHANGE
     */
    createButton(buttonName, opt_smallSized) {
      let buttonLabel = L('NEW_BUTTON');
      let newButtonModel = {
        icon: 'Doc/doc_32.png',
        isCaptionVisible: !opt_smallSized,
        name: buttonName,
        caption: buttonLabel,
        tooltip: buttonLabel,
        isVisible: true,
        isEnabled: true,
      };
      this._model.buttons.push(newButtonModel);
      this.fire(WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT.CHANGE);
      return new global.components.wrappers.toolBar.ReactToolBarButton(newButtonModel);
    },

    /**
     * Сворачивает группу кнопок.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#collapse
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @fires WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT#COLLAPSE
     */
    collapse() {
      this._model.isCollapsed = true;
      this.fire(WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT.COLLAPSE);
      return this;
    },

    /**
     * Пошагово сжимает группу кнопок.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#fold
     * @param {GROUP_FOLD_MODE} foldMode Режим сжатия.
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @deprecated since 5.6.
     */
    fold(foldMode) {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return this;
    },

    /**
     * Пошагово восстанавливает группу кнопок.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#unfold
     * @param {GROUP_FOLD_MODE} foldMode Режим сжатия.
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @deprecated since 5.6.
     */
    unfold(foldMode) {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return this;
    },

    /**
     * Сжимает группу в указанный размер.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#fitToWidth
     * @param {integer} width Ширина, в которую необходимо вписать группу.
     * @return {boolean} Результат сжатия. True – группы вместилась, иначе False.
     * @deprecated since 5.6.
     */
    fitToWidth(width) {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return true;
    },

    /**
     * Разворачивает кнопки группы.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#expand
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @fires WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT#EXPAND
     */
    expand() {
      this._model.isCollapsed = false;
      this.fire(WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT.EXPAND);
      return this;
    },

    /**
     * Признак свернутой группы кнопок.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#isCollapsed
     * @return {boolean} True, если группа свернута, иначе False.
     */
    isCollapsed() {
      return this._model.isCollapsed;
    },

    /**
     * Получает ширину группы.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#getWidth
     * @return {number} Всегда 0, так как компонента не знает ничего о представлении.
     * @deprecated since 5.6.
     */
    getWidth() {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return 0;
    },

    /**
     * Получает список кнопок текущей группы.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#getButtons
     * @return {WebAccess.components.wrappers.toolBar.Buttons} Список объектов типа ReactToolBarButton, вложенных в эту группу.
     */
    getButtons() {
      let toolbarButtons = global.current.toolBar.buttons.toArray();
      let filteredButtons = toolbarButtons.filter((button) => {
        return button.groupName === this._model.name;
      });
      return new WA.Collection(filteredButtons);
    },

    /**
     * Добавляет нижнюю границу к текущей группе.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#addBottomBorder
     * @param {string} color CSS-класс цвета нижней границы.
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @deprecated since 5.6.
     */
    addBottomBorder(color) {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return this;
    },

    /**
     * Скрывает следующий за группой разделитель.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#hideNextSeparator
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @deprecated since 5.6.
     */
    hideNextSeparator() {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return this;
    },

    /**
     * Показывает следующий за группой разделитель.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBarGroup#showNextSeparator
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     * @deprecated since 5.6.
     */
    showNextSeparator() {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      return true;
    }
  }, {
    EVENT: {
      // Событие, срабатывающее при сворачивании группы.
      COLLAPSE: 'toolbarGroupCollapse',
      // Событие, срабатывающее при разворачивании группы.
      EXPAND: 'toolbarGroupExpand',
      // Событие, срабатывающее при изменении модели группы.
      CHANGE: 'toolbarGroupChange'
    }
  });

  global.components.wrappers.toolBar.ReactToolBarGroup = ReactToolBarGroup;
})(window.WebAccess);